import { useState } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import { useCreateZendeskTicketMutation, useLazyGetCountryNameQuery } from 'app/api/gatewayApiSlice';
import { normalizeTag } from 'actions/utils';
import { Input, Select } from 'semantic-ui-react';
import './contactForm.scss';
import { useAuth } from 'context/AuthContext';
import { useTranslation } from 'i18n/LocaleContext';
import { useGetProfileQuery } from 'app/api/rcpApiSlice';
import { styleListMarkers } from './richText';

const MAX_DETAILS_LENGTH = 1000;

function toHtml(html, options = {}) {
  return { __html: styleListMarkers(DOMPurify.sanitize(html || '', options)) };
}

function toPlainText(html) {
  return DOMPurify.sanitize(html || '', { ALLOWED_TAGS: [] }).trim();
}

function escapeHtml(str = '') {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttribute(str = '') {
  return escapeHtml(str).replace(/"/g, '&quot;');
}

const CUSTOM_FIELD_IDS = {
  productNameId: Number(process.env.REACT_APP_ZENDESK_PRODUCT_NAME_FIELD_ID),
  requestTypeId: Number(process.env.REACT_APP_ZENDESK_REQUEST_TYPE_FIELD_ID),
  countryNameId: Number(process.env.REACT_APP_ZENDESK_COUNTRY_NAME_FIELD_ID),
};
function Field({
  label,
  required,
  error,
  children,
}) {
  return (
    <div className='flex flex-col gap-[8px] '>
      <label className='text-[14px] font-medium text-black md:text-[18px]'>
        {label}
        {required && <span className='text-red-500'>*</span>}
      </label>

      {children}

      {error && (
        <p className='text-[12px] text-red-500'>
          {error}
        </p>
      )}
    </div>
  );
}
export default function ContactForm({ data, isLoggedIn = false }) {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [createZendeskTicket] = useCreateZendeskTicketMutation();
  const [getCountryName] = useLazyGetCountryNameQuery();
  const { user, signinRedirect } = useAuth();
  const idToken = user?.id_token;

  const [form, setForm] = useState({
    email: user?.profile.email || '',
    subject: '',
    details: '',
  });

  const { data: profileData } = useGetProfileQuery(idToken, {
    skip: !idToken,
  });

  const cultureCode = profileData?.user?.cultureCode || 'en-US';

  const subjectOptions = [
    {
      value: 'I have a billing issue.',
      label: t(
        'RVS.COMPONENT.CONTACT_US.OPTION_BILLING_ISSUE',
        'I have a billing issue.',
      ),
    },
    {
      value: 'I found a bug.',
      label: t(
        'RVS.COMPONENT.CONTACT_US.OPTION_FOUND_BUG',
        'I found a bug.',
      ),
    },
    {
      value: 'I have a specific issue.',
      label: t(
        'RVS.COMPONENT.CONTACT_US.OPTION_SPECIFIC_ISSUE',
        'I have a specific issue.',
      ),
    },
    {
      value: 'Other...',
      label: t(
        'RVS.COMPONENT.CONTACT_US.OPTION_OTHER',
        'Other...',
      ),
    },
  ];

  function inputClass(hasError) {
    return [
      'w-full rounded-[20px] border border-[rgba(0,0,0,0.5)] bg-white',
      'px-[14px] md:px-[24px]',
      'text-[14px] md:text-[16px]',
      'leading-[22px] text-black',
      'placeholder:text-[rgba(0,0,0,0.5)]',
      'outline-none transition',
      'focus:border-[#39A9F7]',
      hasError ? 'border-red-500 bg-red-50' : '',
    ].join(' ');
  }

  function handleChange(e) {
    const { name, value } = e.target;

    if (name === 'details' && value.length > MAX_DETAILS_LENGTH) return;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  }

  function validate() {
    const next = {};

    if (!isLoggedIn) {
      if (!form.email.trim()) {
        next.email = t('RVS.COMPONENT.CONTACT_US.VALIDATION.EMAIL_IS_REQUIRED', 'Email is required.');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        next.email = t('RVS.COMPONENT.CONTACT_US.VALIDATION.INVALID_EMAIL', 'Please enter a valid email.');
      }
    }

    if (!form.subject) next.subject = t('RVS.COMPONENT.CONTACT_US.VALIDATION.SUBJECT_IS_REQUIRED', 'Please select a subject.');
    if (!form.details.trim()) next.details = t('RVS.COMPONENT.CONTACT_US.VALIDATION.DETAILS_IS_REQUIRED', 'Details are required.');

    return next;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    const next = validate();

    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    setSubmitting(true);
    setErrors({});

    try {
      const countryName = await getCountryName().unwrap();

      const customFields = [
        {
          id: CUSTOM_FIELD_IDS.productNameId,
          value: 'Roland Cloud',
        },
        {
          id: CUSTOM_FIELD_IDS.requestTypeId,
          value: 'product_support',
        },
        {
          id: CUSTOM_FIELD_IDS.countryNameId,
          value: countryName,
        },
      ];

      const payload = {
        brand: 'roland',
        ticket: {
          type: 'question',
          custom_fields: customFields,
          tags: [
            'roland_cloud',
            normalizeTag(countryName),
            normalizeTag(cultureCode),
          ],
        },
        request: {
          subject: `${form.subject} Roland Cloud`,
          comment: {
            body: form.details,
          },
        },
      };

      await createZendeskTicket({
        payload,
        idToken,
      }).unwrap();

      setSubmitted(true);
    } catch (error) {
      setErrors({
        submit:
          error?.data?.message
          || t('RVS.COMPONENT.CONTACT_US.NOTIFICATION.EXCEPTION_ERROR', 'Something went wrong. Please try again.'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderSubtitle = () => {
    if (!data?.subtitle) return null;

    if (
      data?.faqUrl
      && data?.faqLinkText
      && data.subtitle.includes(data.faqLinkText)
    ) {
      const faqUrl = escapeAttribute(data.faqUrl || 'https://support.roland.com/hc/en-us/search?query=roland+cloud');
      const faqLinkText = escapeHtml(data.faqLinkText);
      const linkedSubtitle = data.subtitle.replace(
        data.faqLinkText,
        `<a href="${faqUrl}" target="_blank" rel="noreferrer" class="text-[#39A9F7] underline">${faqLinkText}</a>`,
      );
      return (
        <p
          className='contact-page-rte mx-auto mt-[16px] max-w-[620px] text-center text-[14px] leading-[24px] text-black md:text-[18px] md:leading-[32px]'
          dangerouslySetInnerHTML={toHtml(linkedSubtitle, { ADD_ATTR: ['target'] })}
        />
      );
    }

    return (
      <p
        className='contact-page-rte mx-auto mt-[16px] max-w-[620px] text-center text-[14px] leading-[24px] text-black md:text-[18px] md:leading-[32px]'
        dangerouslySetInnerHTML={toHtml(data.subtitle)}
      />
    );
  };

  if (submitted) {
    return (
      <section className='mt-[60px] md:mt-[80px] text-center'>
        <h2
          className='contact-page-rte text-[26px] font-bold leading-[30px] text-black md:text-[38px] md:leading-[44px]'
          dangerouslySetInnerHTML={toHtml(
            data?.successTitle || t('RVS.COMPONENT.CONTACT_US.SUCCESS_TITLE', 'Message sent!'),
          )}
        />

        <p
          className='contact-page-rte mx-auto mt-[16px] max-w-[520px] text-[14px] leading-[24px] text-black md:text-[18px] md:leading-[32px]'
          dangerouslySetInnerHTML={toHtml(
            data?.successBody
              || t('RVS.COMPONENT.CONTACT_US.SUCCESS_BODY', "We've received your message and will get back to you shortly."),
          )}
        />
      </section>
    );
  }

  if (!isLoggedIn) {
    return (
      <section className='mt-[60px] md:mt-[80px]'>
        <div className='mx-auto flex max-w-[620px] flex-col items-center gap-[40px] text-center'>
          <div>
            <h2
              className='contact-page-rte text-[26px] font-bold leading-[30px] text-black md:text-[38px] md:leading-[44px]'
              dangerouslySetInnerHTML={toHtml(
                data?.title || t('RVS.COMPONENT.CONTACT_US.TITLE', 'Still need help?'),
              )}
            />
            {renderSubtitle()}
          </div>

          <div className='flex w-full flex-col gap-[16px] md:flex-row md:justify-center md:gap-[45px]'>
            <button
              type='button'
              onClick={() => signinRedirect()}
              aria-label={toPlainText(
                data?.loginText || t('RVS.COMPONENT.CONTACT_US.BUTTON.LOG_IN', 'Log In'),
              )}
              className='
    inline-flex h-[46px] w-full items-center justify-center
    rounded-[36px] bg-[#39A9F7]
    px-[24px]
    text-[16px] font-medium text-white
    transition hover:bg-[#2c9be8] hover:text-white
    md:w-[200px] md:text-[18px] md:font-bold
  '
            >
              <span
                className='contact-page-rte'
                dangerouslySetInnerHTML={toHtml(
                  data?.loginText || t('RVS.COMPONENT.CONTACT_US.BUTTON.LOG_IN', 'Log In'),
                )}
              />
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className='mt-[60px] md:mt-[80px]'>
      <div className='text-center'>
        <h2
          className='contact-page-rte text-[26px] font-bold leading-[30px] text-black md:text-[38px] md:leading-[44px]'
          dangerouslySetInnerHTML={toHtml(
            data?.title || t('RVS.COMPONENT.CONTACT_US.TITLE', 'Still need help?'),
          )}
        />
        {renderSubtitle()}
      </div>

      <div className='mx-auto mt-[30px] max-w-[812px]'>
        <form
          noValidate
          onSubmit={handleSubmit}
          className='flex flex-col gap-[32px]'
        >
          <Field label={t('RVS.COMPONENT.CONTACT_US.LABEL.EMAIL', 'Email')} required error={errors.email}>
            <Input
              type='email'
              name='email'
              value={form.email}
              disabled
              tabIndex={-1}
              transparent={false}
              className='contact-readonly-input'
              autoComplete='email'
            />
          </Field>

          <Field label={t('RVS.COMPONENT.CONTACT_US.LABEL.SUBJECT', 'Subject')} required error={errors.subject}>
            <Select
              name='subject'
              value={form.subject}
              onChange={(e, { name, value }) => handleChange({
                target: {
                  name,
                  value,
                },
              })}
              options={subjectOptions.map((option) => ({
                key: option.value,
                value: option.value,
                text: option.label,
              }))}
              placeholder={t(
                'RVS.COMPONENT.CONTACT_US.SUBJECT_PLACEHOLDER',
                'Subject line',
              )}
              className={errors.subject ? 'contact-select error' : 'contact-select'}
              fluid
              selection
            />
          </Field>

          <Field label={t('RVS.COMPONENT.CONTACT_US.LABEL.DETAILS', 'Details')} required error={errors.details}>
            <div className='relative'>
              <textarea
                name='details'
                value={form.details}
                onChange={handleChange}
                placeholder={t('RVS.COMPONENT.CONTACT_US.PLACEHOLDER.DETAILS', 'Please provide as much details as you can!')}
                rows={8}
                className={`${inputClass(
                  errors.details,
                )} min-h-[284px] resize-none pt-[30px] pb-[36px]`}
              />

              <span className='absolute bottom-[12px] right-[16px] text-[12px] text-[rgba(0,0,0,0.5)] md:text-[14px]'>
                {form.details.length}
                {' '}
                /
                {MAX_DETAILS_LENGTH}
              </span>
            </div>
          </Field>

          {errors.submit && (
            <p className='text-center text-[13px] text-red-500'>
              {errors.submit}
            </p>
          )}

          <div className='flex justify-center'>
            <button
              type='submit'
              disabled={submitting}
              className='
                inline-flex h-[46px] items-center justify-center w-full md:w-auto
                rounded-[36px] bg-[#39A9F7]
                px-[24px]
                text-[16px] font-medium text-white
                transition hover:bg-[#2c9be8] hover:text-white
                disabled:cursor-not-allowed disabled:opacity-60
                md:text-[18px] md:font-bold
              '
            >
              {submitting
                ? t('RVS.COMPONENT.CONTACT_US.BUTTON.SENDING', 'Sending...')
                : t('RVS.COMPONENT.CONTACT_US.BUTTON.SUBMIT', 'Submit')}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  error: PropTypes.string,
  children: PropTypes.node.isRequired,
};

Field.defaultProps = {
  required: false,
  error: undefined,
};

ContactForm.propTypes = {
  isLoggedIn: PropTypes.bool,
  data: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    faqUrl: PropTypes.string,
    faqLinkText: PropTypes.string,
    submitUrl: PropTypes.string,
    submitText: PropTypes.string,
    loginUrl: PropTypes.string,
    loginText: PropTypes.string,
    subjectPlaceholder: PropTypes.string,
    detailsPlaceholder: PropTypes.string,
    defaultEmail: PropTypes.string,
    successTitle: PropTypes.string,
    successBody: PropTypes.string,
    subjectOptions: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      }),
    ),
  }),
};

ContactForm.defaultProps = {
  isLoggedIn: false,
  data: null,
};
